"use strict";
// packages/shared-schemas/src/crmStageMapping.ts
//
// Unified pipeline stage mapping per CRM provider.
//
// Plan: ~/.claude/plans/this-was-a-full-idempotent-cerf.md (PR-3)
Object.defineProperty(exports, "__esModule", { value: true });
exports.CrmStageOverrideSchema = exports.CRM_PROVIDERS = exports.UNIFIED_STAGES = void 0;
exports.toUnifiedStage = toUnifiedStage;
exports.toUnifiedStageWithOverrides = toUnifiedStageWithOverrides;
exports.rawStatusesForUnifiedStage = rawStatusesForUnifiedStage;
const zod_1 = require("zod");
//
// Background
// ----------
// Different CRMs use different vocabularies for "where is this deal in
// the pipeline":
//   MyCRM     : 'New' / 'Pre-Approved' / 'Conditional Approval' /
//               'Lodged' / 'Submitted' / 'Approved' / 'Settled' /
//               'Withdrawn' / 'Declined'
//   Nodify    : (TBC — sandbox creds pending)
//   Monday    : group + column-status combo per board template
//   Smokeball : matter status enum
//
// Cross-CRM analytics need a single bird's-eye view:
//   new → contacted → qualified → deal_won / deal_lost
// (matches Valutten's existing lead.status enum so the UX is uniform.)
//
// This module is the canonical mapping. Every CRM provider exports a
// per-deal stage via toUnifiedStage(provider, rawStatus). The
// reporting layer joins on the unified stage rather than the raw
// status, so queries like getConversionBySource() work across any
// subscriber's CRM mix.
//
// Subscribers MAY override the mapping via
//   companies/{id}/crm_stage_overrides/{provider}
// (e.g. "treat 'Submitted to Lender' as qualified, not contacted").
// The override doc shape mirrors this registry — admin UI lands in PR-3
// frontend.
exports.UNIFIED_STAGES = [
    'new', // Lead captured, no broker interaction yet
    'contacted', // Broker reached out (call, email, meeting booked)
    'qualified', // Application submitted / pre-approval / conditional
    // — broker has done the work to qualify the prospect
    'deal_won', // Deal settled / matter closed successfully
    'deal_lost', // Withdrawn / declined / lost-to-competitor
    'unknown', // Status not yet mapped or genuinely missing
];
exports.CRM_PROVIDERS = ['mycrm', 'nodify', 'monday', 'smokeball'];
const MYCRM_MAP = {
    // New / inquiry
    'new': 'new',
    'inquiry': 'new',
    'enquiry': 'new',
    'lead': 'new',
    // Contacted / in-progress
    'contacted': 'contacted',
    'in progress': 'contacted',
    'fact find': 'contacted',
    'fact find scheduled': 'contacted',
    'fact find completed': 'contacted',
    'application started': 'contacted',
    // Qualified — submitted to lender or pre-approved
    'submitted': 'qualified',
    'submitted to lender': 'qualified',
    'lodged': 'qualified',
    'pre-approved': 'qualified',
    'pre approved': 'qualified',
    'conditional approval': 'qualified',
    'conditionally approved': 'qualified',
    'approved': 'qualified',
    'unconditional approval': 'qualified',
    'awaiting settlement': 'qualified',
    // Won
    'settled': 'deal_won',
    'completed': 'deal_won',
    'funds disbursed': 'deal_won',
    // Lost
    'withdrawn': 'deal_lost',
    'declined': 'deal_lost',
    'rejected': 'deal_lost',
    'cancelled': 'deal_lost',
    'lost': 'deal_lost',
    'expired': 'deal_lost',
    'not proceeding': 'deal_lost',
};
// Nodify map TBC — placeholder. When sandbox creds land we'll populate
// this from their docs + sample data. Until then any Nodify deal lands
// as 'unknown' which is detectable in the reporting layer.
const NODIFY_MAP = {};
// Monday.com — board-template-specific. The Monday provider will pass
// the column-status text in directly; we map common patterns from the
// board templates Australian mortgage brokers most commonly use:
//   - CRM (Monday's flagship CRM template)
//   - Lead Management
//   - Sales Pipeline
//   - Property listings
//   - Project / Generic Done-style
// Custom statuses fall through to 'unknown' until the subscriber adds
// an override via companies/{id}/crm_stage_overrides/monday.
const MONDAY_MAP = {
    // ── CRM template ────────────────────────────────────────────────
    // Monday's flagship CRM uses the classic "New → Qualifying →
    // Qualified → Negotiating → Won/Lost" funnel.
    'new': 'new',
    'qualifying': 'contacted', // broker actively working the lead
    'qualified': 'qualified',
    'negotiating': 'qualified', // commercial terms in flight = pre-settle
    'won': 'deal_won',
    'lost': 'deal_lost',
    // ── Lead Management template ────────────────────────────────────
    'untouched lead': 'new', // Monday's default for fresh-import leads
    'new lead': 'new',
    'attempted to contact': 'contacted', // call placed even if not connected
    'contacted': 'contacted',
    'qualified lead': 'qualified',
    'unqualified lead': 'deal_lost', // explicitly disqualified = lost
    'unqualified': 'deal_lost',
    'converted': 'deal_won', // converted to customer/deal
    // ── Sales Pipeline template ─────────────────────────────────────
    // Standard B2B sales funnel; brokers occasionally adopt this for
    // commercial / asset-finance pipelines.
    'prospecting': 'new',
    'discovery call': 'contacted',
    'demo': 'contacted', // demo booked = active engagement
    'proposal sent': 'qualified', // proposal = formal offer in market
    'negotiation': 'qualified',
    'closed won': 'deal_won',
    'closed lost': 'deal_lost',
    // ── Property listings template ──────────────────────────────────
    // Some brokers track buyer-agent referrals or property-purchase
    // pipelines on Monday. Map the listing-status vocabulary.
    'new listing': 'new',
    'inspection scheduled': 'qualified', // post-inspection = pre-application stage
    'inspection booked': 'qualified',
    'offer received': 'qualified', // mortgage broker: offer accepted ≈ pre-approval window
    'under contract': 'qualified', // contract exchanged, awaiting settlement
    'settled': 'deal_won', // property settled
    'withdrawn': 'deal_lost', // listing withdrawn / deal pulled
    // ── Project / Generic Done-style template ───────────────────────
    // Monday's default status column on every new board.
    'working on it': 'contacted', // active engagement, generic equivalent of "in progress"
    'in progress': 'contacted',
    'stuck': 'qualified', // blocked but not lost — broker working through it
    'on hold': 'qualified', // paused awaiting docs / decision, not lost
    'done': 'deal_won',
    'pending review': 'qualified', // common Done-template variant
    // ── Cross-template synonyms ─────────────────────────────────────
    // Generic vocabulary that crops up across multiple templates and
    // subscriber customisations.
    'open': 'new',
    'follow up': 'contacted',
    'follow-up': 'contacted',
    'meeting booked': 'contacted',
    'meeting scheduled': 'contacted',
    'application': 'qualified',
    'application submitted': 'qualified',
    'pre-approval': 'qualified',
    'pre approval': 'qualified',
    'approved': 'qualified', // approved-but-not-settled = qualified
    'declined': 'deal_lost',
    'rejected': 'deal_lost',
    'cancelled': 'deal_lost',
    'lost - no response': 'deal_lost',
    'not interested': 'deal_lost',
    'duplicate': 'deal_lost', // duplicate lead culled
};
// Smokeball — matter status. Legal matters open / active / closed.
const SMOKEBALL_MAP = {
    'open': 'new',
    'active': 'contacted',
    'pending': 'qualified',
    'closed': 'deal_won',
    'cancelled': 'deal_lost',
    'archived': 'deal_won', // Smokeball archives won matters
};
const REGISTRY = {
    mycrm: MYCRM_MAP,
    nodify: NODIFY_MAP,
    monday: MONDAY_MAP,
    smokeball: SMOKEBALL_MAP,
};
/**
 * Map a per-CRM raw status to the unified pipeline stage.
 *
 * Normalises the raw status (lowercase + trimmed) before lookup so
 * casing / trailing-whitespace variance from the upstream CRM doesn't
 * miss the map. Unknown statuses return 'unknown' — the reporting
 * layer can surface those for admin attention via subscriber overrides.
 *
 * @param provider  Which CRM the raw status came from
 * @param rawStatus The status string as the CRM stores it
 * @returns         The unified stage
 *
 * @example
 *   toUnifiedStage('mycrm', 'Settled')              // 'deal_won'
 *   toUnifiedStage('mycrm', 'Conditional Approval') // 'qualified'
 *   toUnifiedStage('mycrm', '(unknown)')            // 'unknown'
 *   toUnifiedStage('mycrm', null)                   // 'unknown'
 */
function toUnifiedStage(provider, rawStatus) {
    if (!rawStatus)
        return 'unknown';
    const normalised = String(rawStatus).trim().toLowerCase();
    if (normalised === '' || normalised === '(unknown)')
        return 'unknown';
    return REGISTRY[provider][normalised] ?? 'unknown';
}
/**
 * Apply a subscriber-specific override layered on top of the canonical
 * map. Used when a tenant has tagged a custom status (e.g.
 * 'Submitted to Lender (Custom)') and recorded the desired unified
 * stage in companies/{id}/crm_stage_overrides/{provider}.
 *
 * @param provider  CRM provider
 * @param rawStatus The raw status from the CRM
 * @param overrides Map<lowercased-raw, UnifiedStage> from the override doc
 * @returns The resolved unified stage
 */
function toUnifiedStageWithOverrides(provider, rawStatus, overrides) {
    if (!rawStatus)
        return 'unknown';
    const normalised = String(rawStatus).trim().toLowerCase();
    if (normalised === '' || normalised === '(unknown)')
        return 'unknown';
    return (overrides?.[normalised] ??
        REGISTRY[provider][normalised] ??
        'unknown');
}
/**
 * Reverse lookup: which raw statuses in this CRM map to a given
 * unified stage. Used by the reporting layer to construct CRM-specific
 * filters when summarising across providers.
 */
function rawStatusesForUnifiedStage(provider, stage) {
    const map = REGISTRY[provider];
    return Object.entries(map)
        .filter(([, s]) => s === stage)
        .map(([raw]) => raw);
}
// Schema for the per-tenant override doc — admin UI persists into
// companies/{id}/crm_stage_overrides/{provider}.
//   { mappings: { "submitted to lender (custom)": "qualified", … } }
//
// IMPORTANT: z.enum() takes its tuple LITERAL inline, not via reference
// to UNIFIED_STAGES. Two prior issues forced this:
//   (a) `import { z } from 'zod'` mid-file shifted the require() emission
//       order — webpack hit `zod_1.z is undefined` at module init for
//       any consumer of this file (white screen on stage 2026-04-27).
//   (b) `z.enum(UNIFIED_STAGES)` with the `as const` array passed
//       fine through TS but webpack's tree-shake + readonly tuple
//       handling could leave the runtime zod call with malformed input.
// Both fixed here: import is at top of file, and the enum tuple is
// inlined. The UNIFIED_STAGES const stays for runtime use elsewhere.
exports.CrmStageOverrideSchema = zod_1.z.object({
    mappings: zod_1.z.record(zod_1.z.string().min(1).max(120), zod_1.z.enum([
        'new', 'contacted', 'qualified', 'deal_won', 'deal_lost', 'unknown',
    ])).default({}),
    updatedAt: zod_1.z.unknown().optional(),
    updatedBy: zod_1.z.string().nullable().optional(),
});
